import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { breakpoint } from 'src/theme/grid'

const EDGE_OFFSET = 8

const pulse = keyframes`
  0% { box-shadow: 0 2px 6px rgba(0,0,0,0.06); }
  50% { box-shadow: 0 6px 16px rgba(0,0,0,0.18); }
  100% { box-shadow: 0 2px 6px rgba(0,0,0,0.06); }
`

const isOverflowBoundary = overflow =>
  ['auto', 'scroll', 'hidden', 'clip'].includes(overflow)

const getRightBoundary = node => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Number.POSITIVE_INFINITY
  }

  let boundary =
    window.innerWidth ||
    document.documentElement.clientWidth ||
    Number.MAX_SAFE_INTEGER
  let parent = node.parentElement

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent)

    if (
      isOverflowBoundary(style.overflow) ||
      isOverflowBoundary(style.overflowX) ||
      isOverflowBoundary(style.overflowY)
    ) {
      boundary = Math.min(boundary, parent.getBoundingClientRect().right)
    }

    parent = parent.parentElement
  }

  return boundary - EDGE_OFFSET
}

const shouldAlignToEnd = node => {
  const anchor = node.offsetParent || node.parentElement

  if (!anchor) {
    return false
  }

  const anchorRect = anchor.getBoundingClientRect()
  const tooltipRect = node.getBoundingClientRect()

  return anchorRect.left + tooltipRect.width > getRightBoundary(node)
}

export const FormInputWrapperWithTooltip = styled.div`
  position: relative;
  padding-bottom: 0;
  margin-bottom: 30px;
  width: 100%;
  margin: 0 0.8rem 30px 0;
  display: inline-block;
  vertical-align: middle;
  max-width: 626px;
  ${breakpoint.m`
    width: 160px;
  `}
`

export const FormInputTooltipBubble = styled.div`
  background-color: ${({ theme }) => theme.colors.california.hex()};
  color: ${({ theme }) => theme.colors.white.hex()};
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  max-width: calc(100vw - ${EDGE_OFFSET * 2}px);
  width: max-content;
  white-space: ${({ $singleLine }) => ($singleLine ? 'nowrap' : 'normal')};
  position: relative;
  animation: ${pulse} 1.8s ease-in-out infinite;

  a {
    color: inherit;
    text-decoration: underline;
  }
  /* ogonek w lewym górnym rogu */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 8px;
    transform: translateY(-100%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-bottom: 6px solid ${({ theme }) => theme.colors.california.hex()};
  }
`

const FormInputTooltipContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: auto;
  transform: none;
  pointer-events: ${({ $interactive }) => ($interactive ? 'auto' : 'none')};
  z-index: 3;
  margin-top: -5px;
  ${({ $alignToEnd }) =>
    $alignToEnd &&
    css`
      left: auto;
      right: 0;

      ${FormInputTooltipBubble}::before {
        left: auto;
        right: 8px;
      }
    `}
`

export const FormInputTooltip = ({ children, ...props }) => {
  const tooltipRef = useRef(null)
  const [alignToEnd, setAlignToEnd] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const updatePlacement = () => {
      const node = tooltipRef.current

      if (!node) {
        return
      }

      const nextAlignToEnd = shouldAlignToEnd(node)

      setAlignToEnd(current =>
        current === nextAlignToEnd ? current : nextAlignToEnd
      )
    }

    const schedule =
      window.requestAnimationFrame ||
      (callback => window.setTimeout(callback, 0))
    const cancel =
      window.cancelAnimationFrame || (frameId => window.clearTimeout(frameId))
    const frameId = schedule(updatePlacement)

    window.addEventListener('resize', updatePlacement)

    return () => {
      cancel(frameId)
      window.removeEventListener('resize', updatePlacement)
    }
  }, [children])

  return (
    <FormInputTooltipContainer
      ref={tooltipRef}
      $alignToEnd={alignToEnd}
      {...props}
    >
      {children}
    </FormInputTooltipContainer>
  )
}
